import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useLocation, useNavigate } from 'react-router';
import {
  MessageSquare,
  Users,
  Phone,
  Star,
  BarChart3,
  Settings,
  Megaphone,
  Activity,
  FlaskConical,
  Keyboard,
} from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';
import { useWebSocket } from '../../providers/WebSocketProvider';
import { ROUTE_CONFIG, canAccessRoute, canAccessRouteAsRole } from '../../router';
import { apiGet } from '../../hooks/useApi';
import { hasPermission } from '../../../lib/permissions';
import { KeyboardShortcutsModal } from '../shared/KeyboardShortcutsModal';

// Map icon names to components
const ICON_MAP: Record<string, React.ElementType> = {
  MessageSquare,
  Users,
  Phone,
  Activity,
  Star,
  BarChart3,
  Settings,
  FlaskConical,
};

export function Sidebar() {
  const { user, effectiveRole, isImpersonating, portalView } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { subscribe } = useWebSocket();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Global hotkey — Ctrl+/ (Cmd+/ on Mac) toggles the shortcuts modal from
  // any page. No conflicts: the Queue Console handler ignores all Ctrl/Cmd
  // combos, and browsers don't reserve Ctrl+/.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        setShowShortcuts((v) => !v);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Portal view (agency owner, or platform-ops impersonating one): no inbox
  // badge — the portal plus Feedback & Support is the whole app for them.
  const portalOnly = portalView;

  // Highlight the Feedback & Support pill when its page is open.
  const supportActive = location.pathname.startsWith('/dev-support');

  // Hydrate once on mount; after that the WS subscriptions below keep the
  // badge current. The old 30s poll was belt-and-suspenders — both paths
  // hit the same REST endpoint, so removing the timer cuts half the load
  // without changing behavior.
  useEffect(() => {
    if (portalOnly) return;
    apiGet<{ unread_count: number }>('/api/inbox/unread-count')
      .then((data) => setUnreadCount(data.unread_count || 0))
      .catch(() => {});
  }, [portalOnly]);

  // Update on WS message — debounced so an inbound SMS burst (msg.received
  // + N x msg.status + unread.updated) collapses to a single request.
  useEffect(() => {
    if (portalOnly) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const reload = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        apiGet<{ unread_count: number }>('/api/inbox/unread-count')
          .then((data) => setUnreadCount(data.unread_count || 0))
          .catch(() => {});
      }, 200);
    };
    const unsub1 = subscribe('msg.received', reload);
    const unsub2 = subscribe('unread.updated', reload);
    return () => {
      if (timer) clearTimeout(timer);
      unsub1();
      unsub2();
    };
  }, [subscribe, portalOnly]);

  // Filter nav items by role. When impersonating, filter by the effective
  // role — the nav shows exactly what that role would see (the UserSwitcher
  // in the header stays as the only extra).
  const visibleRoutes = ROUTE_CONFIG.filter((route) =>
    isImpersonating
      ? canAccessRouteAsRole(user, route, effectiveRole)
      : canAccessRoute(user, route),
  );

  return (
    <div className="w-16 flex-shrink-0 bg-card border-r border-border/60 flex flex-col items-center py-5 z-20 shadow-[2px_0_8px_rgba(0,0,0,0.02)]">
      {/* Brand logo */}
      <div className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center mb-8 shadow-sm border border-border/60">
        <img src="/brand/apex-icon.svg" alt="Apex Pro" width="20" height="20" />
      </div>

      {/* Navigation — filtered by role */}
      <div className="flex-1 w-full flex flex-col items-center gap-3">
        {visibleRoutes.map((route) => {
          const Icon = ICON_MAP[route.icon] || Settings;
          const isActive = location.pathname.startsWith(route.path);
          const showBadge = route.path === '/inbox' && unreadCount > 0;
          return (
            <button
              key={route.path}
              onClick={() => navigate(route.path)}
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 group relative ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              <Icon className="w-[18px] h-[18px] stroke-[1.5]" />
              {isActive && (
                <motion.div
                  layoutId="nav-indicator"
                  className="absolute -left-0.5 top-1/2 -translate-y-1/2 h-5 w-1 bg-primary rounded-r-full"
                />
              )}
              {/* Unread badge */}
              {showBadge && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-destructive text-white text-[0.5625rem] font-bold flex items-center justify-center border-2 border-card">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              {/* Tooltip */}
              <div className="absolute left-14 bg-foreground text-background px-2.5 py-1.5 text-[0.6875rem] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 shadow-lg z-50 rounded-md translate-x-[-4px] group-hover:translate-x-0">
                {route.label}
                {showBadge && ` (${unreadCount})`}
              </div>
            </button>
          );
        })}
      </div>

      {/* Bottom rail — keyboard shortcuts help + Feedback & Support */}
      <div className="mt-auto flex flex-col items-center w-full">
        <button
          onClick={() => setShowShortcuts(true)}
          aria-label="Keyboard shortcuts"
          className="w-10 h-10 rounded-xl flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-all duration-200 group relative"
        >
          <Keyboard className="w-[18px] h-[18px] stroke-[1.5]" />
          {/* Tooltip */}
          <div className="absolute left-14 bg-foreground text-background px-2.5 py-1.5 text-[0.6875rem] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 shadow-lg z-50 rounded-md translate-x-[-4px] group-hover:translate-x-0">
            Keyboard shortcuts (Ctrl+/)
          </div>
        </button>

        {/* Feedback & Support — the entry point for issues, bugs, and feature
          requests. Deliberately styled as a labeled color pill (not another
          muted icon) so it reads as "raise anything here", not a generic help
          button. Needs the support.view permission (rep+ and agency_owner),
          resolved per effective role. */}
        {hasPermission('support.view') && (
          <div className="flex flex-col items-center mt-3 pt-4 w-full border-t border-border/60">
            <button
              onClick={() => navigate('/dev-support')}
              aria-label="Feedback & Support — report issues, bugs and feature requests"
              className={`group relative w-14 flex flex-col items-center gap-1 py-2 px-1 rounded-xl transition-all duration-200 ${
                supportActive
                  ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                  : 'bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 hover:bg-primary/15'
              }`}
            >
              <Megaphone className="w-[18px] h-[18px] stroke-[1.5]" />
              <span className="text-[0.5625rem] font-bold leading-[1.1] text-center">
                Feedback &amp; Support
              </span>
              {/* Tooltip — explains scope on hover without a popover/tutorial */}
              <div className="absolute left-16 top-1/2 -translate-y-1/2 bg-foreground text-background px-2.5 py-1.5 text-[0.6875rem] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 shadow-lg z-50 rounded-md translate-x-[-4px] group-hover:translate-x-0">
                Report issues, bugs &amp; feature requests
              </div>
            </button>
          </div>
        )}
      </div>

      <KeyboardShortcutsModal open={showShortcuts} onClose={() => setShowShortcuts(false)} />
    </div>
  );
}
